import { FC, useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { checkSession, authSelector } from 'store';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import Layout from 'components/Layout';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/UI/Loader';

import Login from 'pages/Login';
import NotFoundPage from 'pages/NotFoundPage';

import { AuthRoute } from './AuthRoute';
import { AccessRoute } from './AccessRoute';
import * as routes from './Routes';
import SoftwarePage from 'pages/Software';
import Lwm2mPage from 'pages/Lwm2m';
import Lwm2mDetailsPage from 'pages/Lwm2m/Lwm2mDetails';
import HusqiotBusPage from 'pages/HusqiotBus/HusqiotBusDetails';
import SoftwareUploadPage from 'pages/Software/UploadSoftware';
import HusqiotbusStartPage from 'pages/HusqiotBus';
import SoftwareDetailsPage from 'pages/Software/SoftwareDetails';
import RolloutPage from 'pages/Rollout';
import RolloutDetailsPage from 'pages/Rollout/RolloutDetails';
import GroupDetailsPage from 'pages/Rollout/GroupDetails';
import ConnectivityStartPage from 'pages/Connectivity';
import ConnectivtyDetailsPage from 'pages/Connectivity/ConnectivityDetails';
import { Outlet } from 'react-router';
import { RootRoute } from './RootRoute';
import SubscriptionPage from 'pages/HusqiotBus/SubscriptionDetails';

// Router
const router = createBrowserRouter([
  {
    element: <ErrorBoundary />,
    children: [
      {
        element: (
          <Layout>
            <Outlet />
          </Layout>
        ),
        children: [
          {
            element: <AuthRoute />,
            children: [
              {
                element: <AccessRoute allow="readDeviceAccess" />,
                children: [
                  {
                    path: routes.DEVICES,
                    children: [
                      {
                        index: true,
                        element: <ConnectivityStartPage />,
                      },
                      {
                        path: ':id',
                        element: <ConnectivtyDetailsPage />,
                      },
                    ],
                  },
                  {
                    element: <AccessRoute allow="softwareAccess" />,
                    path: routes.SOFTWARES,
                    children: [
                      {
                        index: true,
                        element: <SoftwarePage />,
                      },
                      {
                        path: ':id',
                        children: [
                          {
                            index: true,
                            element: <SoftwareDetailsPage />,
                          },
                        ],
                      },
                      {
                        path: 'upload',
                        element: <SoftwareUploadPage />,
                      },
                    ],
                  },
                  {
                    element: <AccessRoute allow="rolloutAccess" />,
                    path: routes.ROLLOUTS,
                    children: [
                      {
                        index: true,
                        element: <RolloutPage />,
                      },
                      {
                        path: ':id',
                        element: <RolloutDetailsPage />,
                      },
                      {
                        path: ':rolloutId/groups/:groupId',
                        element: <GroupDetailsPage />,
                      },
                    ],
                  },
                ],
              },
              {
                element: <AccessRoute allow="husqiotBusAccess" />,
                children: [
                  {
                    path: routes.HUSQIOTBUS,
                    children: [
                      {
                        index: true,
                        element: <HusqiotbusStartPage />,
                      },
                      {
                        path: ':id',
                        element: <HusqiotBusPage />,
                      },
                      {
                        path: 'subscription/:id',
                        element: <SubscriptionPage />,
                      },
                    ],
                  },
                ],
              },
              {
                element: <AccessRoute allow="readLwm2mLogsAccess" />,
                children: [
                  {
                    path: routes.LWM2M,
                    children: [
                      {
                        index: true,
                        element: <Lwm2mPage />,
                      },
                      {
                        path: ':id',
                        element: <Lwm2mDetailsPage />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: routes.LOGIN,
        element: <Login />,
      },
      {
        path: routes.ROOT,
        element: <RootRoute />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

const Router: FC = () => {
  const dispatch = useAppDispatch();
  const { authCheck } = useAppSelector(authSelector);

  // Check if active session
  useEffect(() => {
    dispatch(checkSession());
  }, [dispatch]);

  // Show loader until auth check is done
  if (!authCheck) {
    return <Loader center padding />;
  }

  return <RouterProvider router={router} />;
};

export default Router;
